import { useCallback, useEffect, useMemo, useState } from "react";
import { useQpmBuildingListQuery } from "@/services/backend/qpm/buildings/api/building-api";
import {
  Area,
  AreaList,
  Building,
  Level,
} from "@/services/backend/qpm/buildings/types/building";
import { BuildingSelector } from "@/services/backend/qpm/buildings/components/building-selector";
import { LevelSelector } from "@/services/backend/qpm/buildings/components/level-selector";
import { Skeleton } from "@/shared/components/ui/skeleton";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { RequirementLevelComponent } from "@/routes/processes/[processId]/qpm/requirements/[requirementId]/assessment/_components/requirement-level-component";
import { AlertWarning } from "@/shared/components/alerts/alert-warning";
import { Loader2, MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Button } from "@/shared/components/ui/button";
import { useSearchParams } from "react-router";
import { Separator } from "@/shared/components/ui/separator";
import { Label } from "@/shared/components/ui/label";
import {
  useRequirementData,
  useRequirementEdit,
} from "@/services/backend/qpm/requirements/hooks/requirement-context";
import { LevelMonitorSummaryComponent } from "@/routes/processes/[processId]/qpm/requirements/[requirementId]/assessment/_components/level-monitor-summary-component";
import { RequirementDTO } from "@/services/backend/qpm/requirements/types/requirement-dto";
import {
  AreaType,
  LevelType,
} from "@/services/backend/qpm/buildings/types/building-enum";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

export type AssessmentComponentProps = {
  requirement: RequirementDTO;
  customerId: string;
};

type AreaTab = "inside" | "outside";

type AssessmentSelectionCache = {
  buildingId?: string;
  levelId?: string;
  tab?: AreaTab;
};

type MonitorSelectionStats = {
  buildingCounts: Record<string, number>;
  levelCountsByKey: Record<string, number>;
};

const ZERO_UUID = "00000000-0000-0000-0000-000000000000";
const NO_LEVEL_ID = "";

export function AssessmentRoute() {
  const { isLoading } = useRequirementData();
  const { putRequest: request } = useRequirementEdit();

  if (isLoading) {
    return <Loader2 className="animate-spin" />;
  }

  return (
    request && (
      <AssessmentComponent
        requirement={request.requirement}
        customerId={request.requirement.customerId}
      />
    )
  );
}

export function AssessmentComponent({
  requirement,
  customerId,
}: AssessmentComponentProps) {
  const [, setSearchParams] = useSearchParams();
  const [selectedBuilding, setSelectedBuilding] = useState<Building>();
  const [selectedLevel, setSelectedLevel] = useState<Level>();
  const [currentBuildingId, setCurrentBuildingId] = useState<string>();
  const [currentLevelId, setCurrentLevelId] = useState<string>();
  const [selectedAreaTab, setSelectedAreaTab] = useState<"inside" | "outside">(
    "inside",
  );

  const {
    data: buildingList,
    error: errorBuildings,
    isLoading: isLoadingBuildings,
  } = useQpmBuildingListQuery({
    customer: { active: true, values: [customerId] },
    serviceObject: {
      active: true,
      values: [requirement.defaultServiceObjectId!],
    },
  });

  const cacheStorageKey = useMemo(
    () => `qpm.requirement.assessment.selection.${requirement.id}`,
    [requirement.id],
  );

  const monitorStats = useMemo<MonitorSelectionStats>(() => {
    const areaByID = new Map(
      requirement.areaList.map((area) => [area.areaId, area]),
    );
    const buildingCounts: Record<string, number> = {};
    const levelCountsByKey: Record<string, number> = {};

    (requirement.selectedMonitors || []).forEach((selectedMonitor) => {
      const requirementArea = areaByID.get(selectedMonitor.areaId);
      const area = requirementArea?.area;
      if (!area?.buildingId) {
        return;
      }

      const monitorAmount = Math.max(selectedMonitor.amount || 0, 0);
      if (monitorAmount === 0) {
        return;
      }

      const tab: AreaTab =
        area.type === AreaType.AreaTypeOutside ? "outside" : "inside";
      const normalizedLevelID = normalizeLevelID(area.levelId);

      buildingCounts[area.buildingId] =
        (buildingCounts[area.buildingId] || 0) + monitorAmount;

      const levelCountKey = toLevelCountKey(
        area.buildingId,
        tab,
        normalizedLevelID,
      );
      levelCountsByKey[levelCountKey] =
        (levelCountsByKey[levelCountKey] || 0) + monitorAmount;
    });

    return {
      buildingCounts,
      levelCountsByKey,
    };
  }, [requirement.areaList, requirement.selectedMonitors]);

  useEffect(() => {
    if (
      !selectedBuilding &&
      buildingList &&
      buildingList.buildings.length > 0
    ) {
      const cachedSelection = readAssessmentSelectionCache(cacheStorageKey);
      const initialBuilding =
        (cachedSelection?.buildingId
          ? buildingList.buildings.find(
              (building) => building.id === cachedSelection.buildingId,
            )
          : undefined) ||
        selectBuildingByMostMonitors(
          buildingList.buildings,
          monitorStats.buildingCounts,
        ) ||
        buildingList.buildings[0];

      const initialTab: AreaTab =
        cachedSelection?.tab ||
        selectTabByMostMonitors(
          initialBuilding.id,
          monitorStats.levelCountsByKey,
        );
      setSelectedAreaTab(initialTab);

      const cachedLevelID = cachedSelection?.levelId;
      const initialLevel = selectLevelForTab(
        initialBuilding,
        initialTab,
        monitorStats.levelCountsByKey,
        cachedLevelID,
      );

      setSelectedBuilding(initialBuilding);
      setCurrentBuildingId(initialBuilding.id);
      if (initialLevel) {
        setSelectedLevel(initialLevel);
        setCurrentLevelId(initialLevel.base.id);
      } else {
        setSelectedLevel(undefined);
        setCurrentLevelId(undefined);
      }
    }
  }, [buildingList, cacheStorageKey, monitorStats, selectedBuilding]);

  useEffect(() => {
    if (buildingList) {
      const building = buildingList.buildings.find(
        (b) => b.id === currentBuildingId,
      );
      if (building) {
        setSelectedBuilding(building);
        setCurrentBuildingId(building.id);
        if (!currentLevelId) {
          setSelectedLevel(undefined);
          return;
        }
        const currentLevel = building.levels.find(
          (level) => level.base.id === currentLevelId,
        );
        if (currentLevel) {
          setSelectedLevel(currentLevel);
        } else if (building.levels.length > 0) {
          setSelectedLevel(building.levels[0]);
          setCurrentLevelId(building.levels[0].base.id);
        } else {
          setSelectedLevel(undefined);
          setCurrentLevelId(undefined);
        }
      }
    }
  }, [buildingList, currentBuildingId, currentLevelId]);

  useEffect(() => {
    if (!selectedBuilding) {
      return;
    }
    const currentLevels = levelsForTab(selectedBuilding, selectedAreaTab);
    if (
      selectedLevel &&
      currentLevels.some((l) => l.base.id === selectedLevel.base.id)
    ) {
      return;
    }
    if (!currentLevelId) {
      return;
    }
    const preferredLevel = selectLevelForTab(
      selectedBuilding,
      selectedAreaTab,
      monitorStats.levelCountsByKey,
      currentLevelId,
    );
    setSelectedLevel(preferredLevel);
    setCurrentLevelId(preferredLevel?.base.id);
  }, [
    currentLevelId,
    monitorStats.levelCountsByKey,
    selectedAreaTab,
    selectedBuilding,
    selectedLevel,
  ]);

  useEffect(() => {
    if (!selectedBuilding) {
      return;
    }

    writeAssessmentSelectionCache(cacheStorageKey, {
      buildingId: selectedBuilding.id,
      levelId: currentLevelId,
      tab: selectedAreaTab,
    });
  }, [cacheStorageKey, currentLevelId, selectedAreaTab, selectedBuilding]);

  const handleNewBuildingCreateCallback = (building: Building) => {
    setSelectedBuilding(building);
    setCurrentBuildingId(building.id);
    setSelectedLevel(undefined);
    setCurrentLevelId(undefined);
  };

  const handleBuildingSelect = (buildingId: string) => {
    const building = buildingList?.buildings.find((b) => b.id === buildingId);
    if (building && building !== selectedBuilding) {
      const preferredLevel = selectLevelForTab(
        building,
        selectedAreaTab,
        monitorStats.levelCountsByKey,
      );
      setSelectedBuilding(building);
      setCurrentBuildingId(building.id);
      setSelectedLevel(preferredLevel);
      setCurrentLevelId(preferredLevel?.base?.id);
    }
  };

  const handleLevelSelect = (levelId: string) => {
    if (!levelId) {
      setSelectedLevel(undefined);
      setCurrentLevelId(undefined);
      return;
    }
    const level = selectedBuilding?.levels.find((l) => l.base.id === levelId);
    if (level && level !== selectedLevel) {
      setSelectedLevel(level);
      setCurrentLevelId(level.base.id);
    }
  };

  const onLevelCreated = (level: Level) => {
    setSelectedLevel(level);
    setCurrentLevelId(level.base.id);
  };

  const setAreaSegmentStateOpen = useCallback(() => {
    setSearchParams({ open: "true" });
  }, [setSearchParams]);

  const setAreaSegmentStateClosed = useCallback(() => {
    setSearchParams({ open: "false" });
  }, [setSearchParams]);

  const levelMonitorCountByID = useMemo<Record<string, number>>(() => {
    if (!selectedBuilding) {
      return {};
    }

    return levelsForTab(selectedBuilding, selectedAreaTab).reduce<
      Record<string, number>
    >((counts, level) => {
      const key = toLevelCountKey(
        selectedBuilding.id,
        selectedAreaTab,
        level.base.id,
      );
      return {
        ...counts,
        [level.base.id]: monitorStats.levelCountsByKey[key] || 0,
      };
    }, {});
  }, [monitorStats.levelCountsByKey, selectedAreaTab, selectedBuilding]);

  const noLevelMonitorCount = useMemo(() => {
    if (!selectedBuilding) {
      return 0;
    }

    const key = toLevelCountKey(
      selectedBuilding.id,
      selectedAreaTab,
      NO_LEVEL_ID,
    );
    return monitorStats.levelCountsByKey[key] || 0;
  }, [monitorStats.levelCountsByKey, selectedAreaTab, selectedBuilding]);

  if (errorBuildings) {
    return <RTKQueryErrorAlert error={errorBuildings} />;
  }

  if (isLoadingBuildings) {
    return (
      <div className="flex items-center justify-between gap-2 p-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-3">
      {!buildingList ||
        (buildingList.buildings.length === 0 && (
          <AlertWarning title={t("Hinweis")}>
            <div className="flex flex-col">
              <span>{t("Es wurden noch keine Gebäudedaten hinterlegt.")}</span>
              <span>
                {t(
                  "Um mit der Bestandsaufnahme beginnen zu können, legen Sie bitte zuerst ein Gebäude und anschließend einen Bereich an. Ebenen sind optional.",
                )}
              </span>
            </div>
          </AlertWarning>
        ))}
      <div className="flex items-start justify-between">
        <div className="my-2 flex flex-wrap items-start gap-2">
          <BuildingSelector
            buildingList={buildingList}
            selectedBuilding={selectedBuilding}
            handleBuildingSelect={handleBuildingSelect}
            customerId={customerId}
            serviceObjectId={requirement.defaultServiceObjectId}
            onBuildingCreateCallback={handleNewBuildingCreateCallback}
            monitorCountByBuildingId={monitorStats.buildingCounts}
          />
          <div className="grid items-start gap-1.5">
            <Label htmlFor="qpm-area-type-selector">{t("Bereichsart")}</Label>
            <Select
              value={selectedAreaTab}
              onValueChange={(value) => {
                const nextAreaTab = value as AreaTab;
                setSelectedAreaTab(nextAreaTab);
                if (selectedBuilding) {
                  const nextLevel = selectLevelForTab(
                    selectedBuilding,
                    nextAreaTab,
                    monitorStats.levelCountsByKey,
                  );
                  setSelectedLevel(nextLevel);
                  setCurrentLevelId(nextLevel?.base.id);
                }
              }}
            >
              <SelectTrigger
                id="qpm-area-type-selector"
                className="h-10 w-[160px]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inside">{t("Innenbereich")}</SelectItem>
                <SelectItem value="outside">{t("Außenbereich")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <LevelSelector
            levels={
              selectedBuilding
                ? levelsForTab(selectedBuilding, selectedAreaTab)
                : []
            }
            selectedLevel={selectedLevel}
            handleLevelSelect={handleLevelSelect}
            buildingId={selectedBuilding?.id}
            onLevelCreatedCallback={onLevelCreated}
            allowNoLevel
            noLevelLabel={t("Ohne Ebene")}
            createLevelType={
              selectedAreaTab === "outside"
                ? LevelType.LevelTypeOutside
                : LevelType.LevelTypeInside
            }
            monitorCountByLevelId={levelMonitorCountByID}
            noLevelMonitorCount={noLevelMonitorCount}
            isNoLevelSelected={!currentLevelId}
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="outline" className="h-8 w-8">
              <MoreVertical className="h-3.5 w-3.5" />
              <span className="sr-only">More</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={setAreaSegmentStateOpen}>
              {t("Bereiche aufklappen")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={setAreaSegmentStateClosed}>
              {t("Bereiche zuklappen")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {selectedBuilding && (
        <div className="space-y-3">
          <RequirementLevelComponent
            buildingName={selectedBuilding.name}
            buildingId={selectedBuilding.id}
            selectedLevel={selectedLevel}
            availableAreas={
              selectedAreaTab === "inside"
                ? collectInsideAreas(selectedBuilding, selectedLevel)
                : collectOutsideAreas(selectedBuilding)
            }
            requirement={requirement}
            areaType={
              selectedAreaTab === "inside"
                ? AreaType.AreaTypeInside
                : AreaType.AreaTypeOutside
            }
          />
          {selectedLevel && selectedAreaTab === "inside" && (
            <div className="flex flex-col gap-2">
              <Separator />
              <LevelMonitorSummaryComponent
                requirement={requirement}
                selectedBuilding={selectedBuilding}
                selectedLevel={selectedLevel}
                selectedLevelAreas={(
                  selectedBuilding.levelledAreas || []
                ).filter((area) => area.levelId === selectedLevel.base.id)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function collectOutsideAreas(selectedBuilding: Building): AreaList {
  const outsideAreas = new Map<string, Area>();

  (selectedBuilding.unlevelledAreas || []).forEach((area) => {
    if (area.type === AreaType.AreaTypeOutside) {
      outsideAreas.set(area.base.id, area);
    }
  });

  (selectedBuilding.levelledAreas || []).forEach((area) => {
    if (area.type === AreaType.AreaTypeOutside) {
      outsideAreas.set(area.base.id, area);
    }
  });

  return Array.from(outsideAreas.values());
}

function collectInsideAreas(
  selectedBuilding: Building,
  selectedLevel?: Level,
): AreaList {
  const insideAreas = new Map<string, Area>();

  if (!selectedLevel) {
    (selectedBuilding.unlevelledAreas || []).forEach((area) => {
      if (area.type !== AreaType.AreaTypeOutside) {
        insideAreas.set(area.base.id, area);
      }
    });
  } else {
    (selectedBuilding.levelledAreas || []).forEach((area) => {
      if (
        area.levelId === selectedLevel.base.id &&
        area.type !== AreaType.AreaTypeOutside
      ) {
        insideAreas.set(area.base.id, area);
      }
    });
  }

  return Array.from(insideAreas.values());
}

function levelsForTab(selectedBuilding: Building, tab: AreaTab): Level[] {
  if (tab === "outside") {
    return (selectedBuilding.levels || []).filter(
      (level) => level.type === LevelType.LevelTypeOutside,
    );
  }
  return (selectedBuilding.levels || []).filter(
    (level) =>
      level.type === LevelType.LevelTypeInside ||
      level.type === LevelType.LevelTypeUndefined,
  );
}

function normalizeLevelID(levelID?: string): string {
  if (!levelID || levelID === ZERO_UUID) {
    return NO_LEVEL_ID;
  }
  return levelID;
}

function toLevelCountKey(
  buildingID: string,
  tab: AreaTab,
  levelID: string,
): string {
  return `${buildingID}|${tab}|${normalizeLevelID(levelID)}`;
}

function selectTabByMostMonitors(
  buildingID: string,
  levelCountsByKey: Record<string, number>,
): AreaTab {
  const prefixInside = `${buildingID}|inside|`;
  const prefixOutside = `${buildingID}|outside|`;

  const { inside, outside } = Object.entries(levelCountsByKey).reduce(
    (acc, [key, count]) => {
      if (key.startsWith(prefixInside)) {
        return { ...acc, inside: acc.inside + count };
      }
      if (key.startsWith(prefixOutside)) {
        return { ...acc, outside: acc.outside + count };
      }
      return acc;
    },
    { inside: 0, outside: 0 },
  );

  if (outside > inside) {
    return "outside";
  }
  return "inside";
}

function selectBuildingByMostMonitors(
  buildings: Building[],
  buildingCounts: Record<string, number>,
): Building | undefined {
  return buildings.reduce<Building | undefined>(
    (selectedBuilding, building) => {
      const monitorCount = buildingCounts[building.id] || 0;
      if (!selectedBuilding) {
        return building;
      }
      const selectedCount = buildingCounts[selectedBuilding.id] || 0;
      if (monitorCount > selectedCount) {
        return building;
      }
      return selectedBuilding;
    },
    undefined,
  );
}

function selectLevelForTab(
  building: Building,
  tab: AreaTab,
  levelCountsByKey: Record<string, number>,
  preferredLevelID?: string,
): Level | undefined {
  const levels = levelsForTab(building, tab);
  const normalizedPreferredLevelID = normalizeLevelID(preferredLevelID);
  if (normalizedPreferredLevelID) {
    return levels.find((level) => level.base.id === normalizedPreferredLevelID);
  }
  if (preferredLevelID === NO_LEVEL_ID) {
    return undefined;
  }

  const selectedLevel = levels.reduce<Level | undefined>(
    (currentSelectedLevel, level) => {
      if (!currentSelectedLevel) {
        return level;
      }
      const monitorCount =
        levelCountsByKey[toLevelCountKey(building.id, tab, level.base.id)] || 0;
      const currentSelectedCount =
        levelCountsByKey[
          toLevelCountKey(building.id, tab, currentSelectedLevel.base.id)
        ] || 0;
      if (monitorCount > currentSelectedCount) {
        return level;
      }
      return currentSelectedLevel;
    },
    undefined,
  );
  const selectedLevelCount = selectedLevel
    ? levelCountsByKey[
        toLevelCountKey(building.id, tab, selectedLevel.base.id)
      ] || 0
    : -1;

  const noLevelCount =
    levelCountsByKey[toLevelCountKey(building.id, tab, NO_LEVEL_ID)] || 0;
  if (selectedLevelCount < noLevelCount) {
    return undefined;
  }

  return selectedLevel;
}

function readAssessmentSelectionCache(
  storageKey: string,
): AssessmentSelectionCache | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const rawCache = window.localStorage.getItem(storageKey);
  if (!rawCache) {
    return undefined;
  }

  try {
    return JSON.parse(rawCache) as AssessmentSelectionCache;
  } catch {
    return undefined;
  }
}

function writeAssessmentSelectionCache(
  storageKey: string,
  cacheValue: AssessmentSelectionCache,
): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(storageKey, JSON.stringify(cacheValue));
}
